<template>
  <section class="hero-banner">
    <div class="hero-banner-container">
      <div class="hero-banner-copy">
        <h1 class="hero-banner-title">
          <UIAnimatedText :text="title" effect="rotate" :delay="50" :duration="600" />
        </h1>

        <p class="hero-banner-description">
          {{ description }}
        </p>

        <div class="hero-banner-actions">
          <UIButton
            class="hero-banner-cta"
            variant="primary-arrow"
            @click="onApplicationFormClick"
          >
            {{ buttonText }}
          </UIButton>
        </div>
      </div>

      <div class="hero-banner-media" aria-hidden="true">
        <HeroImageRotator :images="images" :alt="title" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

const currentLocale = computed(() => (route.params.locale ?? locale.value) as string)

interface Props {
  title?: string
  description?: string
  buttonText?: string
  buttonLink?: string
  image?: string
}

const props = withDefaults(defineProps<Props>(), {
  buttonLink: '',
  image: '/assets/images/index/freepik__background__80583 2@2x.png'
})

const title = computed(() => props.title ?? t('index.hero.title'))
const description = computed(() => props.description ?? t('index.hero.description'))
const buttonText = computed(() => props.buttonText ?? t('index.hero.startCourseButton'))

function onApplicationFormClick (e: MouseEvent) {
  e.preventDefault()
  const homePath = `/${currentLocale.value}`
  if (route.path === homePath) {
    const el = document.getElementById('application-form-section')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  } else {
    router.push(homePath).then(() => {
      nextTick(() => {
        document.getElementById('application-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    })
  }
}

const BANNER = '/assets/images/home-page/banner'
const LCP_NAME = 'cholovik-trymaie-bibliiu-v-rukakh-izoliat'

useHead({
  link: [
    {
      rel: 'preload',
      as: 'image',
      type: 'image/webp',
      href: `${BANNER}/x1/webp/${LCP_NAME}.webp`,
      fetchpriority: 'high',
    },
  ],
})

function heroImg(name: string, alt: string) {
  return {
    src: `${BANNER}/x1/${name}.png`,
    alt,
    webpMobile: `${BANNER}/mobile/webp/${name}.webp 341w, ${BANNER}/x1/webp/${name}.webp 681w`,
    webpSrcset: `${BANNER}/x1/webp/${name}.webp 681w, ${BANNER}/x2/webp/${name}.webp 1361w`,
  }
}

const images = [
  heroImg('cholovik-trymaie-bibliiu-v-rukakh-izoliat', 'Молодий чоловік в окулярах та блакитному худі тримає відкриту Біблію на прозорому фоні'),
  heroImg('shchaslyva-simia-mandrivnyky-obijmy-izoliat', 'Усміхнена сімʼя туристів або мандрівників: тато, мама в капелюсі та донька з кавою на прозорому фоні'),
  heroImg('uspishnyi-zrilatyi-cholovik-sorochka-izoliat', 'Впевнений зрілий чоловік у блакитній сорочці посміхається та тримає руки разом на прозорому фоні'),
  heroImg('viiskovyi-pokazuie-paltsiamy-vhoru-izoliat', 'Український військовий у камуфляжній кепці посміхається та вказує пальцями вгору на прозорому фоні'),
  heroImg('divchyna-molytsia-zakryti-ochi-izoliat', 'Брюнетка в рожевому светрі із закритими очима склала руки для молитви на прозорому фоні'),
  heroImg('studentka-ruzhovolosa-knyhy-navchannia-izoliat', 'Рудоволоса дівчина-студентка в картованій сорочці тримає книги та посміхається на прозорому фоні'),
]
</script>

<style scoped>
.hero-banner {
  width: 100%;
  position: relative;
  overflow: visible;
}

.hero-banner-container {
  box-sizing: border-box;
  width: 100%;
  margin: 0 auto;
  max-width: 1600px;
  padding: 140px 0px 0 137px;
  display: grid;
  grid-template-columns: minmax(0, 745px) minmax(0, 1fr);
  align-items: center;
}

.hero-banner-copy {
  display: grid;
  gap: clamp(16px, 2vw, 24px);
  min-width: 0;
  align-self: center;
  margin-bottom: 186px;
}

.hero-banner-title {
  font-family: var(--font-inter);
  font-weight: 700;
  font-size: clamp(32px, 4.2vw, 60px);
  line-height: 1.3;
  color: var(--color-dark-blue);
  margin: 0;
  overflow: visible;
  min-width: 0;
}

/* .hero-banner-title :deep(.swiper-parallax-fancy-text) {
  line-height: inherit;
} */

.hero-banner-description {
  font-family: var(--font-inter);
  font-size: clamp(22px, 1.2vw, var(--font-size-body));
  line-height: 1.6;
  color: var(--color-grey);
  margin: 0;
}

.hero-banner-actions {
  display: flex;
  justify-content: flex-start;
}

.hero-banner-media {
  width: 100%;
  justify-self: stretch;
  align-self: end;
  min-width: 0;
}

@media (min-width: 1200px) {
  .hero-banner-container {
    grid-template-columns: minmax(0, 745px) minmax(630px, 1fr);
  }
}

@media (max-width: 1199px) {
  .hero-banner-container {
    grid-template-columns: minmax(0, 1fr) minmax(320px, 44vw);
    padding: 180px 24px 96px;
  }
}

@media (max-width: 900px) {
  .hero-banner-container {
    display: flex;
    flex-direction: column;
    padding: 190px 24px 0;
    row-gap: 0;
  }

  .hero-banner-copy {
    justify-items: center;
    text-align: center;
    margin-bottom: 24px;
  }

  .hero-banner-actions {
    justify-content: center;
  }

  .hero-banner-media {
    max-width: 520px;
    margin: 0 auto;
    width: 100%;
  }
}

@media (max-width: 768px) {
  .hero-banner-container {
    padding: 160px 20px 0;
  }
  .hero-banner-container{
    padding: 180px 15px 0;
  }

  .hero-banner-actions {
    width: 100%;
  }

  .hero-banner-media {
    max-width: 520px;
  }

  .hero-banner-cta {
    width: 100%;
    max-width: none;
    display: flex;
    justify-content: center;
    margin: 0 auto;
  }
}
@media (max-width: 768px) and (min-width: 480px) {
  .hero-banner-container{
    max-width: 85%;
    padding: 160px 0px 73px 0px;

  }
  .hero-banner-description {
    font-size: clamp(18px, 1.2vw, var(--font-size-body));
  }
  .hero-banner-copy{
    text-align: left;
    justify-content: left;
  }
  .hero-banner-actions {
    display: flex;
    width: auto;
    font-size: 18px;
    justify-content: left;
  }
  .hero-banner-title {
    text-align: left;
    font-size: clamp(32px, 5.6vw, 60px);
    line-height: 1.1;
  }
  .hero-banner-copy {
    justify-items: left;
  }
}
@media (max-width: 660px) {
  .hero-banner-container {
    padding: 122px 16px 0;
  }

  .hero-banner-media {
    height: 450px;
  }
}
@media (max-width: 480px) {
  .hero-banner-container {
    padding: 122px 16px 0;
    row-gap: 8px;

  }

  .hero-banner-copy {
    justify-items: start;
    text-align: left;
    padding: 0 43px;
    margin-bottom: 0;
  }

  .hero-banner-actions {
    width: 100%;
    justify-content: flex-start;
    margin-top: 15px;
  }

  .hero-banner-media {
    max-width: 360px;
    margin: 0 auto;
    height: 390px;
  }
  .hero-banner-description {
  line-height: 1.45;
  }
  .hero-banner-cta {
    width: 100%;
    max-width: none;

  }
}
@media (max-width: 460px) {
  .hero-banner-copy {
    padding: 0 10px;
    padding: 10px 16px 0;
  }
}
</style>
